import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import type { DisclosureAction, DisclosureVariant } from '../../types/index.js';
import styles from './loquix-disclosure-badge.styles.js';

/** Maps disclosure actions to human-readable verbs. */
const ACTION_VERBS: Record<DisclosureAction, string> = {
  generated: 'Generated by',
  summarized: 'Summarized by',
  rewrote: 'Rewritten by',
  suggested: 'Suggested by',
};

/**
 * @tag loquix-disclosure-badge
 * @summary Small badge indicating AI involvement, e.g. "Generated by Claude".
 *
 * @csspart badge - The badge element.
 *
 * @cssprop [--loquix-disclosure-bg] - Background colour.
 * @cssprop [--loquix-disclosure-text-color] - Text colour.
 * @cssprop [--loquix-disclosure-border-radius] - Border radius (default pill).
 * @cssprop [--loquix-disclosure-font-size] - Font size.
 * @cssprop [--loquix-disclosure-padding] - Padding inside the badge.
 */
export class LoquixDisclosureBadge extends LitElement {
  static styles = [styles];

  /** Name of the AI agent displayed after the action verb. */
  @property({ type: String, attribute: 'agent-name', reflect: true })
  agentName = 'AI';

  /** The action that the AI performed. */
  @property({ type: String, reflect: true })
  action: DisclosureAction = 'generated';

  /** Visual variant of the badge. */
  @property({ type: String, reflect: true })
  variant: DisclosureVariant = 'label';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  protected render() {
    const verb = ACTION_VERBS[this.action] ?? ACTION_VERBS.generated;
    const text = `${verb} ${this.agentName}`;

    return html`
      <span part="badge" class="badge" role="note" aria-label="${text}">
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path
              d="M12 2l2.4 7.4H22l-6.2 4.5L18.2 22 12 17.5 5.8 22l2.4-8.1L2
                 9.4h7.6z"
            />
          </svg>
        </span>
        <span class="label">${text}</span>
      </span>
    `;
  }
}
